import store from "@/store"
import { GraphicComponentTypes, IGraphicGDM, PlacementTypes, UGCData } from "@/libs/graphics/types/Graphics.t"
import { setUGCData, UGCDataStore, getAllUGCData, getUGCDataByImageURL } from "@/libs/graphics/UGCData"
import PatternScaleCalculation from "./PatternScaleCalculation"

export interface FetchAndInsertImageProps {
    src: string
    componentID?: PlacementTypes
    subcomponentID?: GraphicComponentTypes
    width?: number
    height?: number
    type?: 'ugc' | 'pattern'
    imgId?: string
    imgUrl?: string
    swatch_id?: number | string
    useHREF?: boolean
}

/**
 * The function takes in a src string, fetches the image from the src, processes the image dimensions,
 * and then sets the image data in the store
 * @param {FetchAndInsertImageProps} props - FetchAndInsertImageProps
 */
export const fetchAndInsertImageToGraphicSettings = async (
    props: FetchAndInsertImageProps
) => {
    const dataInStore = getUGCDataByImageURL(props.imgUrl)
    const componentID = props.componentID ?? 'graphic_front'
    const subcomponentID = props.subcomponentID ?? 'fill_ugc'
    const imgId = props.imgId
    const imgUrl = props.imgUrl
    const type = props.type ?? 'pattern'
    const base64 = dataInStore?.ugcData?.href ?? await fetchImage(props.src)
    const patternData = dataInStore?.ugcData ?? (props.width && props.height ? {
        href: base64,
        width: props.width,
        height: props.height,
        original_image: imgUrl,
    } : await processImageDimensions(base64, imgUrl))
    const swatch_id = props.swatch_id ?? "11021"
    const graphic: IGraphicGDM = store.getters['gdm/getGraphicByComponent'](componentID)

    // define the compononent group as UGC in the active recipeV2 state
    if (type === 'ugc') {
        // reset any previously stored ugc placement data

        if (graphic?.id)
            store.commit("graphicsController/CLEAR_UGC_DATA_PER_PLACEMENT", {
                placement: componentID,
                graphicId: graphic.id
            });

        store.commit('swatches/SET_SWATCHES_SELECTED', {
            component: `${ componentID }/${ subcomponentID }`,
            data: {
                edited: true,
                group: 'ugc',
                subgroup: 'ugc',
                swatchId: swatch_id,
            },
        });

        store.dispatch("recipe/UPDATE_RECIPE_COMPONENT", {
            componentName: `${componentID}/${subcomponentID}`,
            group: "ugc",
            material_type: "svg",
            subgroup: "ugc",
            swatchId: swatch_id,
            ugc: {
                size: "0.0",
                tonal_stitch_color: "NA",
                original_image: {
                    image_id: imgId,
                    image_url: imgUrl
                }
            }
        });

        setUGCData({
            ugcData: { ...patternData, image_id: imgId },
            subcomponentID,
            componentID
        });
        store.commit("graphicsController/SET_PLACEMENT_SETTING", {
            placementComponent: componentID,
            componentSetting: {
                id: subcomponentID,
                has_ugc: true,
                swatch_id,
                group: "ugc",
                material_type: "svg",
                subgroup: "ugc",
                ugc: {
                    size: "0.0",
                    tonal_stitch_color: "NA",
                    original_image: {
                        image_id: imgId,
                        image_url: imgUrl
                    }
                }
            },
            type: "user_defined",
        })
    }

    if (type === 'pattern') {
        const scaledPatternData = PatternScaleCalculation({
            pdmModel: (store.state as any).utils.pdmModel,
            componentID,
            patternData,
            graphic
        })

        store.commit("graphicsController/SET_PLACEMENT_SETTING", {
            placementComponent: componentID,
            componentSetting: { id: subcomponentID, pattern: scaledPatternData, swatch_id: swatch_id },
            type: "user_defined",
        })
    }
}

/**
 * `fetchImage` is a function that takes an image url and returns a base64 string of the image
 * @param {string} url - The URL of the image to fetch.
 * @returns A string of the base64 encoded image.
 */
const fetchImage = async (url: string): Promise<string> => {
    const response = await fetch(url)
    const blob = await response.blob()
    const base64 = await transformBlobToBase64(blob) as string
    return base64
}

/**
 * It takes a blob and returns a promise that resolves to a base64 string
 * @param {Blob} blob - The blob to be converted to base64.
 * @returns A promise that resolves to a base64 string.
 */
const transformBlobToBase64 = (blob: Blob) => {
    return new Promise((resolve, reject) => {
        let reader = new FileReader()
        reader.onload = (e) => {
            resolve(e.target!.result)
        };
        reader.onerror = (e) => {
            reject(e)
        }
        reader.readAsDataURL(blob)
    })
}

/**
 * It takes a base64 string and returns an object with the image's width, height, and href
 * @param {string} base64 - The base64 string of the image
 * @param {string} [hrefOverride] - This is the URL of the image. If you don't provide this, the base64
 * string will be used.
 * @returns A promise that resolves to an object with a width, height, and href property.
 */
const processImageDimensions = async (base64: string, original_image?: string): Promise<UGCData> => {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
            resolve({
                width: img.width,
                height: img.height,
                href: base64,
                original_image
            })
        }
        img.onerror = reject
        img.src = base64
    })
}